import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2, X, MessageSquare, Instagram, Facebook, Linkedin, Youtube, Globe } from "lucide-react";

const PLATFORM_ICONS = { Instagram, Facebook, LinkedIn: Linkedin, YouTube: Youtube, Website: Globe };

export default function PortalApprovals() {
  const { summary, reload } = useOutletContext();
  if (!summary) return <div className="p-8 text-neutral-500">Loading…</div>;
  const items = summary.stats.pending_approvals;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1300px]">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Sign-off Queue</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Approvals</h1>
        <p className="text-neutral-600 text-sm mt-1">{items.length} {items.length===1?"post":"posts"} awaiting your review</p>
      </div>
      {items.length === 0 ? (
        <div className="panel p-10 text-center text-neutral-500">
          <div className="inline-flex w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-700"/></div>
          <div className="text-lg font-bold text-neutral-900">All caught up</div>
          <div className="text-sm mt-1">Nothing awaiting your approval right now.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(c => <ApprovalCard key={c.id} c={c} onDone={reload}/>)}
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ c, onDone }) {
  const [note, setNote] = useState("");
  const [showRev, setShowRev] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comments, setComments] = useState(c.comments || []);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const Icon = PLATFORM_ICONS[c.platform] || Globe;

  const act = async (kind) => {
    setBusy(true);
    try {
      const url = kind === "approve" ? `/contents/${c.id}/approve` : `/contents/${c.id}/request-revision`;
      await api.post(url, { note });
      toast.success(kind === "approve" ? "Approved & scheduled" : "Changes sent to team");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/contents/${c.id}/comments`, { body: newComment.trim() });
      setComments(cs => [...cs, data]);
      setNewComment("");
      toast.success("Comment posted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  return (
    <div className="panel p-5" data-testid={`approval-${c.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center shrink-0">
          <Icon className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold">{c.content_topic || c.content_type}</div>
            <span className="text-[10px] font-mono uppercase text-neutral-500">{c.platform} · {c.content_type}</span>
            <span className="text-[10px] font-mono text-neutral-500">· {c.publishing_date} {c.publishing_time}</span>
          </div>
          {c.caption && <div className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap">{c.caption}</div>}
          {c.hashtags && <div className="text-[11px] font-mono text-orange-700 mt-1 break-words">{c.hashtags}</div>}
          {c.cta && <div className="text-[11px] font-mono text-emerald-700 mt-1">CTA: {c.cta}</div>}
        </div>
        <StatusPill status={c.status}/>
      </div>

      <div className="mt-4 pt-4 border-t border-[#E4E7EC]">
        <div className="flex items-center gap-2 mb-2 text-[10px] font-mono uppercase tracking-wider text-neutral-500">
          <MessageSquare className="w-3 h-3"/> Comments ({comments.length})
        </div>
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {comments.length === 0 && <div className="text-[11px] font-mono text-neutral-500 italic">No comments yet — leave a note for your team.</div>}
          {comments.map(cm => (
            <div key={cm.id} className="flex gap-2" data-testid={`comment-${cm.id}`}>
              <div className={`w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold shrink-0 ${cm.author_role === "client" ? "bg-gradient-to-br from-orange-500/40 to-red-500/30" : "bg-gradient-to-br from-emerald-500/40 to-cyan-500/30"}`}>
                {(cm.author_name || "?").split(" ").map(x=>x[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 rounded-md border border-[#E4E7EC] bg-[#FFFFFF] px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-xs font-semibold">{cm.author_name}</div>
                  <span className="text-[9px] font-mono uppercase text-neutral-500">{cm.author_role}</span>
                  <span className="text-[9px] font-mono text-neutral-500">· {new Date(cm.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-neutral-800 mt-0.5 whitespace-pre-wrap">{cm.body}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Textarea rows={2} value={newComment} onChange={e=>setNewComment(e.target.value)} placeholder="Add a comment..." data-testid={`comment-input-${c.id}`} className="bg-[#FFFFFF] border-[#E4E7EC] flex-1 text-sm"/>
          <Button onClick={postComment} disabled={posting || !newComment.trim()} size="sm" data-testid={`comment-post-${c.id}`} className="bg-orange-500 hover:bg-orange-600 text-white self-end">
            <Send className="w-3.5 h-3.5 mr-1"/> Post
          </Button>
        </div>
      </div>

      {showRev && (
        <div className="mt-3">
          <Textarea rows={3} value={note} onChange={e=>setNote(e.target.value)} placeholder="Tell the team what to change..." data-testid={`revision-note-${c.id}`} className="bg-[#FFFFFF] border-[#E4E7EC]"/>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        {!showRev ? (
          <>
            <Button variant="outline" className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10" onClick={()=>setShowRev(true)} data-testid={`btn-revise-${c.id}`}>
              <X className="w-4 h-4 mr-1"/> Request Changes
            </Button>
            <Button onClick={()=>act("approve")} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-neutral-900" data-testid={`btn-approve-${c.id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1"/> Approve &amp; Schedule
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={()=>{ setShowRev(false); setNote(""); }}>Cancel</Button>
            <Button onClick={()=>act("revise")} disabled={busy || !note.trim()} className="bg-rose-500 hover:bg-rose-600 text-neutral-900" data-testid={`btn-send-revision-${c.id}`}>
              <Send className="w-4 h-4 mr-1"/> Send to Team
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
